import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue';
import ProviderPromotionOffer from '@/components/ProviderPromotionOffer.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import TrailerModal from '@/components/TrailerModal.vue';
import { SnowplowLinkContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { getBackdropClips, getSelfHostedTrailerClips } from '@/helpers/title-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { AdPlacement, ClipProvider, MonetizationType, SraPlacement } from '@/@types/graphql-types';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { OfferLocation } from '@/helpers/offer-helper';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { afterNextPaint } from '@/helpers/async-helper';
import { useImpressionPixel } from '@/features/impression-pixels/composables/useImpressionPixel';
import { useProviderPromotion } from '@/helpers/provider-promotion';
import { useJWTVUtopia } from '@/helpers/ad/useJWTVUtopiaTargeting';
import { useTitleSqtvTargeting } from '@/helpers/ad/useTitleSqtvTargeting';

// SEASON_PAGE_BUYBOX_EXP
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutUrl, getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useGetSrPlacement } from '@/features/sponsored-recommendations/composables/useGetSrPlacement';
import adminConfig from '@/features/promotions/config.json';
import { useActivateExperiment } from '@/libraries/experiment';
// SEASON_PAGE_BUYBOX_EXP

import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { generateUUIDv4 } from '@/helpers/uuid-helper';
import { VideoPlayersTrackingIds } from '@/constants/video-players-tracking-ids';
import PictureCompBackdrop from '@/pages/titleDetailV1/v1/hero/PictureCompBackdrop.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailHeroV1',
  props: {
    title: null,
    originalTitle: null,
    seasonName: { default: null },
    AVODOffer: null,
    activeClipId: { default: null },
    isModal: { type: Boolean },
    additionalContexts: { default: () => [] },
    showChartInfo: { type: Boolean }
  },
  emits: ['setActiveClipId', 'closeTitleDetailModal'],
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, originalTitle?: string, seasonName: string | null, AVODOffer?: TitleOfferFragment | null, activeClipId: string | null, isModal?: boolean, additionalContexts: SnowplowContext[], showChartInfo: boolean };

const JustWatchTVPlayBtn = () => import('./JustwatchTVPlayBtn.vue');





const { country, webLocale } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const { xServeTrack } = storeToRefs(useAppStore());
const { isLoggedIn } = storeToRefs(useUserStore());

useProvideClickoutPlacement('backdrop');

const { titleHasBackdropPromo } = useProviderPromotion(() => props.title);
const { isJWTVUtopiaActive } = useJWTVUtopia(() => props.title);
const { isTitleTargetedSqtvActive } = useTitleSqtvTargeting(() => props.title);

// EXP_ATV_QTV_TRAILER_MODAL
const atvQtvActivationCondition = computed(
	() =>
		!props.title.offers.some(offer => offer.package.shortName === 'atp') &&
		!titleHasBackdropPromo.value &&
		!isJWTVUtopiaActive.value &&
		!isTitleTargetedSqtvActive.value
);
useActivateExperiment('EXP_ATV_QTV_TRAILER_MODAL', atvQtvActivationCondition);
// EXP_ATV_QTV_TRAILER_MODAL

const isTrailerModalQtvActive = computed(() => {
	if (
		!adminConfig?.steering_overrides_us?.trailer_modal_qtv ||
		!adminConfig.steering_overrides_us.trailer_modal_provider ||
		!(adminConfig.steering_overrides_us.trailer_modal_markets ?? ([] as String[])).includes(country.value)
	)
		return false;

	return props.title.offers.some(
		offer => offer.package.shortName === adminConfig.steering_overrides_us.trailer_modal_provider
	);
});

// We wanna hide the trailer ads when there is a survey campaign linking to our title page.
const hideTrailerAd = computed(() => {
	return activeRoute.value?.query['utm_campaign']?.includes('SRV');
});

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].monetizationType === MonetizationType.Free &&
		props.title.justwatchTVOffers[0].package.shortName === 'jwt'
);

// ** SRs Adserver Migration ** //
const backdropVideoSrConfig = adminConfig?.ad_server?.srs?.placements?.backdrop_video_sr;
const useAdserverBackdropSrMutation = computed(
	() => backdropVideoSrConfig?.all_countries || backdropVideoSrConfig?.countries?.includes(country.value)
);

const { sponsoredAd } = useAdserverBackdropSrMutation.value
	? useGetSrPlacement({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.BackdropVideo,
			adPlacement: AdPlacement.BackdropVideoSr,
	  })
	: useSendBidId({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.BackdropVideo,
	  });

// **** SRs Backdrop Experiment ****
const hasUserClickedOnTrailerBackdrop = ref(false);
// **** SRs Backdrop Experiment ****

const titleClips = computed(() => {
	const PlexClip = props.AVODOffer
		? [
				{
					provider: 'PLEX',
					externalId: props.AVODOffer.id,
					sourceUrl: props.AVODOffer.standardWebURL,
				},
		  ]
		: null;

	const OttOffer = props.title.offers.find(
		offer => offer.package.technicalName === 'ottstudioplayer' && !!offer.streamUrl
	);
	const OttClip = OttOffer
		? [
				{
					provider: 'OTT',
					externalId: OttOffer.id,
					sourceUrl: OttOffer.streamUrl,
				},
		  ]
		: null;

	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl:
						props.title.justwatchTVOffers[0].streamUrlExternalPlayer ??
						props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	// Self-hosted JWTV trailers take priority — they work for all users (no login required)
	// and play through BitmovinPlayer instead of a third-party iframe.
	const selfHostedBackdropClip = getSelfHostedTrailerClips(props.title);
	return JustWatchTVClip || selfHostedBackdropClip || PlexClip || OttClip || getBackdropClips(props.title);
});

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const titleFullPath = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.fullPath : props.title.content.fullPath;
});

const releaseYear = computed(() => {
	return props.title.content.originalReleaseYear;
});

const showTitleId = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show ? props.title.show.id : props.title.id
);

const showOriginalTitle = computed(() => !!props.originalTitle && props.originalTitle !== props.title.content.title);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

// remove the empty backdrop space, if in the backdrop there is
// - no backdrop
// - no play trailer button
// - no play JWTV button
// - no provider promotion button
const isBackdropEmpty = computed(
	() =>
		props.title.content.backdrops.length === 0 && // no backdrop images
		!titleHasBackdropPromo.value && // no provider "play button in backdrop" promotion
		!isJustWatchTV.value && // no JWTV
		titleClips.value.length === 0 // no trailers
);

const isMounted = ref(false);
const hasModalOpened = ref(false);
// we need an additional state that isn't tied to re-renderings, but prevents users from opening multiple modal windows.
const isModalOpening = ref(false);

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const titleDetailHeroV1Ref = ref(null);
useImpressionPixel(titleDetailHeroV1Ref, webLocale, {
	shortNames: () => props.title.offers.map(o => o.package.shortName),
});

const offerForImpression = computed(() => {
	if (isJustWatchTV.value) return props.title.justwatchTVOffers[0];
	if (props.AVODOffer) return props.AVODOffer;

	return null;
});

const observeVisibilityOptions = computed(() => {
	if (offerForImpression.value == null) return null;

	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

// create clickoutUrl for offerImpression tracking. Can pass the created contexts to the track event.
const clickoutUrl = computed(() => {
	if (!offerForImpression.value || titleHasBackdropPromo.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', country.value)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'jwRawEntityId', props.title?.id ?? null)
		.setGenre(props.title?.content.genres)
		.setCanary(xServeTrack.value);
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);
}

async function handleBackdropClick() {
	if (isLoggedIn.value && !isJustWatchTV.value) {
		return;
	}

	// IPN case: trailer modal is one of the heaviest UI pieces we have.
	// that's why we split it into 2 tasks upon interaction:
	// 1) show spinner
	// 2) show modal
	hasModalOpened.value = true;
	await afterNextPaint();

	if (clickoutUrl.value) {
		// TODO: This is not a "clickout" it should be replace with a more relevant event
		trackClickout(clickoutUrl.value);
	}

	openTrailerModal(false);
	// for SR Backdrop Experiment
	hasUserClickedOnTrailerBackdrop.value = true;
}

async function openTrailerModal(nonInteraction: boolean) {
	if (titleClips.value.length === 0 || titleHasBackdropPromo.value) return;
	if (isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		hasModalOpened.value = true; // important to have this after the setTimeout, so the re-rendering is happening afterwards
		const impressionUUID = generateUUIDv4();
		const LinkContext = new SnowplowLinkContext({
			self: impressionUUID,
		});
		TrackingHelper.trackEvent(
			'youtube_started',
			{
				action: props.title.objectType.toLowerCase(),
				label: 'backdrop',
				value: VideoPlayersTrackingIds[titleClips.value[0].provider as ClipProvider],
				nonInteraction,
			},
			[LinkContext]
		);

		emits('setActiveClipId', titleClips.value[0].externalId);
		// wait for modal to be closed again

		await ModalHelper.openModal(
			TrailerModal,
			{
				clips: titleClips.value,
				title: props.title,
				source: 'backdrop',
				additionalContexts: props.additionalContexts,
				playerAdditionalContexts:
					titleClips.value[0].provider === ClipProvider.Justwatchtv
						? [LinkContext]
						: getClickoutUrlContextsArray(clickoutUrl.value),
				sponsoredAd: sponsoredAd.value,
				sraPlacement: SraPlacement.BackdropVideo,
				hideTrailerAd: hideTrailerAd.value,
			},
			{ cssClass: 'trailer-modal', lockScroll: !isTrailerModalQtvActive.value },
			'',
			true,
			[],
			() => (hasModalOpened.value = false)
		);
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

function handleClickTitleDetailLink() {
	// navigation is to same url, so the modal is not destroyed
	if (props.isModal && titleFullPath.value) {
		emits('closeTitleDetailModal');
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: showTitleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);
}

onMounted(() => {
	if (props.activeClipId) {
		emits('setActiveClipId', null);
	}
	isMounted.value = true;
	TrackingHelper.trackEvent('clip_spinner', {
		action: 'finished',
		value: 1,
		property: titleClips.value.length > 0 ? titleClips.value[0].provider : 'none',
		nonInteraction: true,
	});

	// INP: preload the trailer component's JS already for slow devices
	getTrailerComponent(titleClips.value[0]?.provider as ClipProvider);

	setTimeout(() => {
		const query = activeRoute.value?.query!;
		if (query['trailer'] === 'play') {
			openTrailerModal(true);
		}
	});
});

watch(activeRoute, () => {
	emits('setActiveClipId', null);
});

return { __sfc: true,JustWatchTVPlayBtn, props, emits, country, webLocale, activeRoute, xServeTrack, isLoggedIn, titleHasBackdropPromo, isJWTVUtopiaActive, isTitleTargetedSqtvActive, atvQtvActivationCondition, isTrailerModalQtvActive, hideTrailerAd, isJustWatchTV, backdropVideoSrConfig, useAdserverBackdropSrMutation, sponsoredAd, hasUserClickedOnTrailerBackdrop, titleClips, titleName, titleFullPath, releaseYear, showTitleId, showOriginalTitle, onIntersection, isBackdropEmpty, isMounted, hasModalOpened, isModalOpening, imageUrls, titleDetailHeroV1Ref, offerForImpression, observeVisibilityOptions, clickoutUrl, onVisibilityChanged, handleBackdropClick, openTrailerModal, handleClickTitleDetailLink, ProviderPromotionOffer, TitleDetailInfoRow, TitleDetailLink, faPlay, PictureCompBackdrop }
}

})